<template>
  <div v-if="showGrid" class="ag-pre-table">
    <!-- Barra de herramientas: buscador a la izquierda, acciones a la derecha.
         Es un flex y no un `.columns` de Bulma a propósito: los márgenes
         negativos de `.columns` sacaban los botones fuera del ancho de la tabla. -->
    <div class="ag-toolbar" v-if="hasToolbar">
      <!-- Merged search box (free text + group selector in one input), opt-in
           via `mergedSearch`. Mirrors the FRP groups table filter. -->
      <div
        class="ag-toolbar__search ag-toolbar__search--merged"
        v-if="mergedSearch"
      >
        <b-field>
          <b-autocomplete
            v-model="searchTerm"
            :data="groupSuggestions"
            field="value"
            placeholder="Buscar grupo"
            icon="magnify"
            clearable
            open-on-focus
            :max-height="320"
            class="frp-filter-search"
            @typing="onMergedFilterTyping"
            @select="onGroupSelected"
          >
            <template v-if="dataGroupsMenu.length" #header>
              <span class="frp-filter-header">Grupos</span>
            </template>
            <template #default="{ option }">
              <div class="frp-suggestion-row">
                <b-icon
                  icon="account-multiple"
                  size="is-small"
                  class="frp-suggestion-icon"
                ></b-icon>
                <span>{{ option.value }}</span>
              </div>
            </template>
            <template #empty>Sin coincidencias</template>
          </b-autocomplete>
        </b-field>
      </div>
      <div class="ag-toolbar__search" v-else-if="search">
        <p class="control has-icons-left">
          <input
            class="input"
            type="text"
            id="filter-text-box"
            placeholder="Buscar"
            v-on:input="onFilterTextBoxChanged()"
          />
          <span class="icon is-medium is-left">
            <i class="material-icons">search</i>
          </span>
        </p>
      </div>
      <div class="ag-toolbar__actions">
        <component
          v-if="resolvedComponent"
          :is="resolvedComponent"
          :gridApi="gridApi"
        ></component>
        <menu-filter
          v-if="!mergedSearch && menuFilter.active && dataGroupsMenu.length"
          :groups="dataGroupsMenu"
          :gridApi="gridApi"
        ></menu-filter>
        <upload-file v-if="uploadFile" @sendDataFile="sendDataFile"></upload-file>
      </div>
    </div>
    <ag-grid-vue
      :style="gridStyle"
      :class="themeClass"
      @model-updated="onModelUpdated"
      :defaultColDef="defaultColDef"
      :columnDefs="columnDefs"
      :rowData="rowData"
      @grid-ready="onGridReady"
      @grid-size-changed="onGridSizeChanged"
      @first-data-rendered="onFirstDataRendered"
      @row-data-updated="onRowDataUpdated"
      :rowSelection="rowSelection"
      :isRowSelectable="isRowSelectable"
      @selection-changed="onSelectionChanged"
      :pagination="pagination"
      :paginationPageSize="20"
      :includeHiddenColumnsInQuickFilter="true"
      :editType="editType"
      :suppressDragLeaveHidesColumns="true"
      :suppressRowClickSelection="suppressRowClickSelection"
      @cell-value-changed="onCellValueChanged"
      @row-value-changed="onRowValueChanged"
      @cell-clicked="onCellClicked"
      :tooltipShowDelay="tooltipShowDelay"
      :isExternalFilterPresent="isExternalFilterPresent"
      :doesExternalFilterPass="doesExternalFilterPass"
      :pinnedTopRowData="rowPinning"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
/**
 * La documentación del Ag grid es: https://www.ag-grid.com/vue-data-grid/getting-started/
 * Versión: 30.0.0 community - "No es el empresarial, ese es de paga"
 *
 * Comentarios
 *
 * El componente del menuFilter y uploadFile son componentes propios, el cual se crearon para las necesidades de Siete
 *
 * menuFilter...
 *  Crea un menu desplegable donde se listan los diferentes datos de una columna especificada anteriormente
 *  tienen un buscador dentro del componente, este al seleccionar un grupo automáticamente le envía el dato al ag grid
 *  donde se filtra los datos.
 *
 *  estructura de activación:
 *      type: Object
 *      data: {
 *        grupo: String -> Nombre de la columna que desea crearle el filtro *
 *        active: Boolean -> Verdadero o falso para saber si se activa *
 *      }
 *
 * uploadFile...
 *  Crea un menu donde puede cargar un dato, este se enviara automáticamente a una función definida en el componente del ag grid
 *  el cual desde el componente principal se le proporciona la función el cual es "dataUploadFile" y este enviara la data a esa función
 *
 *  estructura de activación:
 *      type: Boolean
 *      data: True o False
 */

import { AgGridVue } from "ag-grid-vue";
import menuFilter from "../frpm/components/menuFilter.vue";
import uploadFile from "../frpm/components/uploadFile.vue";
import TooltipGraphBarHorizontal from "../frpm/components/tooltipGraphBarHorizontal.vue";
import ButtonsActionRelation from "../lideres/components/buttonsActionRelation.vue";
import ButtonsActionEmployee from "../lideres/components/buttonsActionEmployee.vue";
import HeaderDepend from "../lideres/components/headerDependLeader.vue";
import GraphTable from "../cultura_old/Components/graphTable.vue"; // Ver como hacer el cambio en cultura.
import { mapState } from "vuex";
import Vue from "vue";

export default {
  name: "App",
  data() {
    return {
      // Mostrar o ocultar la tabla completa
      showGrid: true,
      // Filas realmente visibles (tras buscar/filtrar); alimenta `autoHeight`.
      displayedRows: 0,
      // Variables con las api del Ag grid
      gridApi: null,
      gridColumnApi: null,
      // Variable donde se define la configuración por defecto de todas las columnas
      defaultColDef: {
        // make every column use 'text' filter by default
        sortable: true,
        cellDataType: false,
      },
      editType: null,
      rowSelection: null,
      isRowSelectable: null,
      dataGroupsMenu: [],
      tooltipShowDelay: 0,
      tooltipHideDelay: 2000,
      selectedDataType: null,
      // Merged search box state (text + group selection in one input)
      searchTerm: "",
      activeGroup: null,
    };
  },
  props: {
    // Array con las columnas definidas para la tabla
    columnDefs: Array,
    // Array con los datos que se cargaran en la tabla
    rowData: {
      type: Array,
      default() {
        return [];
      },
    },
    // Boleano el cual define si tiene paginación
    pagination: {
      type: Boolean,
      default() {
        return true;
      },
    },
    // Ajusta el alto de la tabla al número de filas visibles, hasta `maxHeight`.
    // Pensado para tablas sin paginación: evita el hueco vacío cuando hay pocas
    // filas y aprovecha la pantalla cuando hay muchas.
    autoHeight: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // Tope de alto en píxeles cuando `autoHeight` está activo.
    maxHeight: {
      type: Number,
      default() {
        return 600;
      },
    },
    // Boleano el cual define si tiene buscador
    search: {
      type: Boolean,
      default() {
        return true;
      },
    },
    // Objeto donde se define la columna y que activa el servicio de menuFilter
    menuFilter: {
      type: Object,
      default() {
        return {
          group: "",
          active: false,
        };
      },
    },
    // Boleano el cual define si se activa el boton de cargar datos
    uploadFile: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // Objeto con el nombre del grupo y su valor
    filters: {
      type: Array,
      default() {
        return null;
      },
    },
    // Funciones para definir filtros externos
    isExternalFilterPresent: {
      type: Function,
      default() {
        return () => false;
      },
    },
    doesExternalFilterPass: {
      type: Function,
      default() {
        return () => true;
      },
    },
    // Boleano el cual define si se activa el seleccionar a las columnas que vengan con un dato llamado empty: true o no
    onDeselectEmptyData: {
      type: Boolean,
      default() {
        return false;
      },
    },
    rowPinning: {
      type: Array,
      default() {
        return [];
      },
    },
    // Boleano el cual define si se activa la columna _actions donde se ingresarian los botones, evitando el selectRow en esa columna
    actions: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // String donde se especifica que tipo de selección van a tener las Rows y el límite de selección en tipo Number
    typeSelection: {
      type: String,
      default() {
        return "single";
      },
    },
    // Boleano el cual define si se deshabilita el seleccionar las filas o no
    suppressRowClickSelection: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // Number el cual define la cantidad maxima a seleccionar en la tabla si tiene el checkbox activo
    limitSelection: {
      type: Number,
      default() {
        return 1;
      },
    },
    // String el cual define el tipo de tema que tendra la tabla
    themeClass: {
      type: String,
      default() {
        return "ag-theme-material";
      },
    },
    // Render Components External
    components: {
      type: Object,
    },
    componentMenu: {
      type: Function,
    },
    // Activa el buscador combinado (texto libre + selector de grupos en un solo
    // input), igual al filtro de la tabla de grupos de FRP. Reemplaza al input
    // simple y al botón "Grupos".
    mergedSearch: {
      type: Boolean,
      default() {
        return false;
      },
    },
    // Campo del subgrupo en rowData. Cuando está definido, escribir el nombre de
    // un subgrupo también muestra su grupo padre en el desplegable del buscador
    // combinado.
    searchSubField: {
      type: String,
      default() {
        return "";
      },
    },
  },
  components: {
    AgGridVue,
    menuFilter,
    uploadFile,
    tooltipGraphBarHorizontal: TooltipGraphBarHorizontal,
    buttonsActionRelation: ButtonsActionRelation,
    buttonsActionEmployee: ButtonsActionEmployee,
    HeaderDepend,
    graphTable: GraphTable,
  },
  computed: {
    ...mapState(["isOculto", "isMovil"]),
    // La barra sólo se pinta si hay algo que poner en ella; si no, dejaba un
    // hueco en blanco sobre la tabla.
    hasToolbar() {
      return !!(
        this.mergedSearch ||
        this.search ||
        this.resolvedComponent ||
        this.uploadFile ||
        (this.menuFilter.active && this.dataGroupsMenu.length)
      );
    },
    gridStyle() {
      if (!this.autoHeight) return { width: "100%", height: "400px" };
      // Altos del tema material: cabecera 56 px, fila 48 px. Se deja sitio para
      // una fila y media de más, así se ve que la lista termina ahí.
      const alto = 56 + this.displayedRows * 48 + 8;
      return {
        width: "100%",
        height: Math.min(Math.max(alto, 160), this.maxHeight) + "px",
      };
    },
    // Grupos mostrados en el desplegable del buscador combinado. Un término
    // vacío (o que coincide con el grupo ya seleccionado) lista todos los grupos
    // para poder explorarlos, como el antiguo botón "Grupos"; de lo contrario
    // filtra por lo escrito. Si `searchSubField` está definido, escribir un
    // subgrupo también muestra su grupo padre.
    groupSuggestions() {
      const sortByValue = (list) =>
        [...list].sort((a, b) =>
          String(a.value).localeCompare(String(b.value), "es", {
            sensitivity: "base",
          })
        );
      const term = this.normalizeText(this.searchTerm).trim();
      if (
        !term ||
        (this.activeGroup && term === this.normalizeText(this.activeGroup))
      )
        return sortByValue(this.dataGroupsMenu);
      const sub = this.searchSubField;
      const groupField = this.menuFilter.group;
      const groupsWithMatchingSub = new Set(
        sub && groupField
          ? (this.rowData || [])
              .filter((r) => this.normalizeText(r[sub]).includes(term))
              .map((r) => r[groupField])
          : []
      );
      return sortByValue(
        this.dataGroupsMenu.filter(
          (g) =>
            this.normalizeText(g.value).includes(term) ||
            groupsWithMatchingSub.has(g.value)
        )
      );
    },
    resolvedComponent() {
      if (this.componentMenu) {
        Vue.component("my-dynamic-component", this.componentMenu());
        return "my-dynamic-component";
      }
      return null;
    },
    resolvedComponents() {
      if (this.components) {
        let componentsRender = 0;
        Object.entries(this.components).forEach((entry) => {
          const [key, value] = entry;
          try {
            Vue.component(key, value());
            componentsRender++;
          } catch (e) {
            console.log(e);
          }
        });
        return componentsRender;
      }
      return null;
    },
  },
  beforeMount: function() {
    // Se asigna la selección especificada al crear el componente a la variable correspondiente
    this.rowSelection = this.typeSelection;
    console.log(this.resolvedComponents);
  },
  created() {
    this.editType = "fullRow";
    if (this.onDeselectEmptyData) {
      this.isRowSelectable = (params) => {
        return !!params.data && !params.data.empty;
      };
    }
  },
  methods: {
    onFirstDataRendered: function(params) {
      params.api.sizeColumnsToFit();
      this.groups();
      if (this.filters && this.filters.length) this.selectOne(this.filters);
    },
    onRowDataUpdated() {
      if (this.filters && this.filters.length) this.selectOne(this.filters);
    },
    showData(dataType, notEdit) {
      if (dataType) {
        this.selectedDataType = dataType;
        // Actualiza la vista de AG-Grid al cambiar el tipo de datos seleccionado
        this.gridApi.setColumnDefs(this.getColumnDefs(notEdit));
        this.gridApi.sizeColumnsToFit();
      }
    },
    getColumnDefs(notEdit) {
      // Retorna los datos del tipo actualmente seleccionado (data1 o data2 ...)
      return this.columnDefs.map((row) => {
        if (!notEdit.includes(row.field)) {
          return { ...row, field: this.selectedDataType };
        }
        return row;
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    // `modelUpdated` es el único evento que cubre a la vez carga de datos,
    // búsqueda y filtros, que es justo lo que cambia el número de filas visibles.
    onModelUpdated(params) {
      if (!this.autoHeight) return;
      this.displayedRows = params.api.getDisplayedRowCount();
    },
    onGridSizeChanged(params) {
      if (!this.isMovil) {
        params.api.sizeColumnsToFit();
      } else {
        params.columnApi.autoSizeAllColumns(false);
      }
    },
    onFilterTextBoxChanged() {
      this.gridApi.setQuickFilter(
        document.getElementById("filter-text-box").value
      );
    },
    // Quita acentos/diacríticos y pasa a minúsculas para que las búsquedas sean
    // insensibles a acentos (p. ej. "produccion" coincide con "Producción").
    normalizeText(value) {
      return String(value == null ? "" : value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    // Token de quick-filter para coincidir con un grupo completo. Debe construirse
    // igual que el `getQuickFilterText` de la columna del grupo.
    groupToken(value) {
      return `FILTER_${this.normalizeText(value).replace(/\s+/g, "")}_GRUPO`;
    },
    // Texto libre: quick filter en vivo. Escribir cancela cualquier filtro de grupo.
    onMergedFilterTyping(value) {
      this.activeGroup = null;
      if (this.gridApi)
        this.gridApi.setQuickFilter(this.normalizeText(value));
    },
    // Elegir un grupo del desplegable aplica el quick filter especial de grupo.
    onGroupSelected(option) {
      if (!option) return;
      this.activeGroup = option.value;
      if (this.gridApi)
        this.gridApi.setQuickFilter(this.groupToken(option.value));
    },
    onSelectionChanged(params) {
      // console.log('1',params)
      if (
        !["rowClicked", "api"].includes(params.source) &&
        this.validateSelection(params)
      ) {
        const selectedRows = this.gridApi.getSelectedRows();
        this.$emit("selected", selectedRows);
      }
    },
    groups() {
      const group = this.menuFilter.group;
      if (group && this.gridApi) {
        let values = [];
        let data = [];
        this.gridApi.forEachNode((node) => {
          if (node.data[group] !== undefined) {
            const exist = values.includes(node.data[group]);
            if (!exist) values.push(node.data[group]);
            if (!exist)
              data.push({ value: String(node.data[group]), active: false });
          }
        });
        this.dataGroupsMenu = data;
      }
    },
    sendDataFile(data, res) {
      this.$emit("dataUploadFile", data);
    },
    deselectAll() {
      this.gridApi.deselectAll();
    },
    deselectOne(filter) {
      // `filter` es un array de objetos clave; se deselecciona toda fila cuyos
      // datos coincidan con todas las claves de alguno de ellos (p. ej.
      // { group, name } en clima o { name, subGrupo } en otras tablas).
      if (!filter || !filter.length) return;
      this.gridApi.forEachNode((node) => {
        for (const key of filter) {
          const match = Object.keys(key).every((k) => node.data[k] === key[k]);
          if (match) node.setSelected(false);
        }
      });
    },
    getSelectedRows() {
      return this.gridApi ? this.gridApi.getSelectedRows() : [];
    },
    selectOne(filter) {
      // Cada elemento de `filter` es un objeto clave; una fila queda seleccionada
      // si sus datos coinciden con todas las claves de alguno de ellos (p. ej.
      // { group, name } en clima o { name, subGrupo } en otras tablas).
      const selected = [];
      const deselected = [];
      this.gridApi.forEachNode((node) => {
        const isRow = filter.some((el) =>
          Object.keys(el).every((k) => node.data[k] === el[k])
        );
        if (isRow) {
          selected.push(node);
        } else {
          deselected.push(node);
        }
      });
      this.gridApi.setNodesSelected({ nodes: selected, newValue: true });
      this.gridApi.setNodesSelected({ nodes: deselected, newValue: false });
    },
    onCellValueChanged(event) {
      /**
       * Retorna el nuevo valor de la columna modificada
       *
       * @response event
       *
       * Log
       * console.log('onCellValueChanged: ' + event.colDef.field + ' = ' + event.newValue);
       * */
    },
    onRowValueChanged(event) {
      /**
       * Retorna el nuevo dato de toda la fila modificada
       *
       * @response event
       *
       * Log
       * console.log(event.data);
       * */
      if (event.data) {
        this.$emit("saveNewDataRow", event.data);
      }
    },
    onCellClicked(params) {
      if (this.actions) {
        let editingCells = params.api.getEditingCells();
        const columnEdit = !!editingCells.some((cell) => {
          return cell.rowIndex === params.node.rowIndex;
        });

        if (!columnEdit) {
          if (params.column.colId === "_actions") {
            // cell is from non-select column
            params.node.setSelected(false);
          } else {
            const selectedRows = this.gridApi.getSelectedRows();
            this.$emit("selected", selectedRows[0]);
          }
        } else {
          params.node.setSelected(false);
        }
      }
    },
    validateSelection(params) {
      const selectedRows = params.api.getSelectedNodes();

      let selectedRowsCount = selectedRows.length;

      if (selectedRowsCount > this.limitSelection) {
        while (selectedRowsCount > this.limitSelection) {
          const node = selectedRows[selectedRowsCount - 1];
          node.setSelected(false);
          selectedRowsCount--;
        }
      }

      return selectedRows;
    },
    resetGrid() {
      this.showGrid = false;
      setTimeout(() => {
        this.showGrid = true;
      }, 10);
    },
  },
  watch: {
    typeSelection(nv) {
      this.rowSelection = nv;
    },
    // Limpiar el input (la "x") quita el filtro de grupo aplicado.
    searchTerm(nv) {
      if (!nv && this.activeGroup) {
        this.activeGroup = null;
        if (this.gridApi) this.gridApi.setQuickFilter();
      }
    },
  },
};
</script>
<style lang="scss">
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-material.css";

.ag-pre-table .ag-theme-material {
  --ag-header-foreground-color: black;
  --ag-font-size: 15px;
  --ag-material-accent-color: #522687;
  --ag-range-selection-border-color: rgba(82, 38, 135, 0.32);
}

/* Barra de herramientas de la tabla. Sin márgenes negativos, para que los
   extremos coincidan exactamente con los de la tabla (que mide el 100%). */
.ag-pre-table .ag-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
}

/* El buscador no necesita todo el ancho: se queda en un tamaño cómodo y cede
   el resto del espacio a las acciones. */
.ag-pre-table .ag-toolbar__search {
  flex: 0 1 340px;
  min-width: 0;
}

/* El buscador combinado lista nombres de grupo, que suelen ser largos. */
.ag-pre-table .ag-toolbar__search--merged {
  flex-basis: 420px;
}

.ag-pre-table .ag-toolbar__search .control,
.ag-pre-table .ag-toolbar__search .field {
  margin-bottom: 0;
}

.ag-pre-table .ag-toolbar__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* En móvil la barra se apila y el buscador ocupa todo el ancho. */
@media screen and (max-width: 768px) {
  .ag-pre-table .ag-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .ag-pre-table .ag-toolbar__search {
    flex: 1 1 auto;
  }

  .ag-pre-table .ag-toolbar__actions {
    margin-left: 0;
    justify-content: center;
  }
}

/* Buscador combinado (texto + grupos), igual al de la tabla de grupos de FRP. */
.frp-filter-search .frp-filter-header {
  display: block;
  padding: 4px 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7a7a7a;
}
.frp-filter-search .frp-suggestion-row {
  display: flex;
  align-items: center;
}
.frp-filter-search .frp-suggestion-icon {
  margin-right: 8px;
  color: #522687;
}
.frp-filter-search .dropdown-item.is-hovered .frp-suggestion-icon,
.frp-filter-search a.dropdown-item:hover .frp-suggestion-icon {
  color: #522687;
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/tableAg.vue